import { AuthenticatedAPIClient } from "./authenticated-api";

export type StatisticsFilterType =
	| "total"
	| "enrollment_status"
	| "payment_status"
	| "paid_only"
	| "unpaid_only"
	| "received_only"
	| "gender_status"
	| "male_only"
	| "female_only";

export interface StatisticsFilter {
	type: StatisticsFilterType;
	limit?: number;
	offset?: number;
	/**
	 * Thứ tự danh sách tiến độ: `count` theo tổng, `rate` theo tỉ lệ đã tiếp nhận.
	 * Phải gửi lên server — với endpoint có phân trang, sắp ở client chỉ đảo được
	 * thứ tự trong trang đang xem chứ không tìm ra hạng nhất thật.
	 */
	sortBy?: "count" | "rate";
	receptionDateFrom?: Date;
	receptionDateTo?: Date;
	appointmentSlot?: string;
	/**
	 * The khoa an unrestricted account narrowed itself to. Sending it from a
	 * scoped account is harmless: the server resolves the scope from the session
	 * and only falls back to this parameter when there is no scope to override.
	 */
	facultyCode?: string;
	/**
	 * Admission cohort ("2026" / "2025"). Suy từ tiền tố `MaSV` trên server —
	 * không có cột `admission_year`. Missing or unrecognised on the server side
	 * simply means "don't filter", never a 400.
	 */
	admissionYear?: string;
}

const HO_CHI_MINH_TIME_ZONE = "Asia/Ho_Chi_Minh";

// Renders a Date's *calendar day* as seen in Asia/Ho_Chi_Minh, regardless of
// the browser's own timezone. The backend expands `receptionDateFrom`/`To`
// to +07 day boundaries, so sending a raw UTC instant (toISOString()) makes
// a viewer outside +07 select a different day than the one they see -- this
// is the bug this function exists to close. `en-CA` is a locale trick: its
// default date format is already `YYYY-MM-DD`.
function toHoChiMinhDateString(date: Date): string {
	return new Intl.DateTimeFormat("en-CA", { timeZone: HO_CHI_MINH_TIME_ZONE }).format(date);
}

export interface StatisticsByDepartment {
	department: string;
	departmentCode: string;
	/** Khoa quản lý ngành này. Không suy ra được từ `departmentCode` — server trả kèm. */
	facultyCode?: string;
	total: number;
	received: number;
	pending: number;
	paid: number;
	unpaid: number;
	receivedButUnpaid: number;
	completed: number; // DaTiepNhan = true AND FeeStatus = 'PAID'
	paidButNotReceived: number; // DaTiepNhan = false AND FeeStatus = 'PAID'
	notReceivedAndNotPaid: number; // DaTiepNhan = false AND FeeStatus != 'PAID'
	male: number;
	female: number;
}

/**
 * Chỉ đếm hồ sơ ĐÃ tiếp nhận.
 *
 * Không có `total` như các tab khác, và đó là chủ ý: danh tính "người tiếp nhận"
 * suy ra từ `LastUpdateUserId` — người sửa cuối — nên một sinh viên chưa (hoặc
 * đã bị gỡ) tiếp nhận vẫn mang tên người vừa chạm vào hồ sơ. Lấy đó làm mẫu số
 * thì hàng "0 / 2" của người vừa gỡ hai hồ sơ trông như người làm việc kém.
 */
export interface StatisticsByApprover {
	approverName: string;
	approverId: string;
	receivedCount: number;
	male: number;
	female: number;
}

export interface StatisticsByProvince {
	provinceCode: string;
	provinceName: string;
	total: number;
	received: number;
	pending: number;
	paid: number;
	unpaid: number;
	paidButNotReceived: number;
	notReceivedAndNotPaid: number;
	percentage: number;
	male: number;
	female: number;
}

export interface StatisticsByHighSchool {
	schoolName: string;
	/**
	 * Tên tỉnh suy từ tiền tố mã trường (KHÔNG phải hộ khẩu sinh viên) — xem
	 * comment ở `be-express/.../statistics.service.ts` `resolveProvinceNamesByPrefix`.
	 * Thiếu khi không tra được, không phải chuỗi rỗng.
	 */
	provinceName?: string;
	total: number;
	received: number;
	pending: number;
	paid: number;
	unpaid: number;
	paidButNotReceived: number;
	notReceivedAndNotPaid: number;
}

export interface StatisticsByAdmissionMethod {
	admissionMethod: string;
	received: number;
	pending: number;
	total: number;
	paid: number;
	unpaid: number;
	paidButNotReceived: number;
	notReceivedAndNotPaid: number;
	male: number;
	female: number;
}

export interface StatisticsByFaculty {
	facultyCode: string;
	facultyName: string;
	total: number;
	received: number;
	pending: number;
	paid: number;
	unpaid: number;
	receivedButUnpaid: number;
	completed: number; // DaTiepNhan = true AND FeeStatus = 'PAID'
	paidButNotReceived: number; // DaTiepNhan = false AND FeeStatus = 'PAID'
	notReceivedAndNotPaid: number; // DaTiepNhan = false AND FeeStatus != 'PAID'
	male: number;
	female: number;
}

export interface GenderDistribution {
	male: number;
	female: number;
	malePercentage: number;
	femalePercentage: number;
	total: number;
}

/**
 * Gói số liệu của `GET /statistics/dashboard` — mọi thứ trang thống kê cần khi
 * tải, đọc trong cùng MỘT ảnh chụp dữ liệu.
 *
 * Mỗi phần có hình dạng y hệt endpoint lẻ tương ứng, nên các component dùng
 * chúng không phải biết dữ liệu đến từ đường nào.
 */
export interface DashboardStatistics {
	overall: OverallStatistics;
	departments: StatisticsByDepartment[];
	faculties: StatisticsByFaculty[];
	approvers: StatisticsByApprover[];
	admissions: StatisticsByAdmissionMethod[];
	gender: GenderDistribution;
}

export interface AgeDistributionData {
	ageGroup: string;
	total: number;
	percentage: number;
	male: number;
	female: number;
}

export interface AwardData {
	code: string;
	awardType: string;
	total: number;
	percentage: number;
	students: {
		name: string;
		studentId: string;
		awardDetails: string;
		daTiepNhan: boolean;
	}[];
}

export interface OverallStatistics {
	totalStudents: number;
	receivedStudents: number;
	pendingStudents: number;
	paidStudents: number;
	unpaidStudents: number;
	receivedButUnpaidStudents: number;
	maleStudents: number;
	femaleStudents: number;
	/** Đã đóng học phí, HOẶC đã tiếp nhận mà học phí chưa về hệ thống. */
	paidOrReceivedStudents: number;
	malePaidOrReceived: number;
	femalePaidOrReceived: number;
	receivedPercentage: string;
	paidPercentage: string;
	malePercentage: string;
	femalePercentage: string;
}

export interface PaginatedResponse<T> {
	data: T[];
	total: number;
	page: number;
	limit: number;
	hasNext: boolean;
	hasPrev: boolean;
}

/** Một ô trong lưới ngày × giờ của tiến trình tiếp nhận. */
export interface ReceptionTimelineBucket {
	date: string;
	/**
	 * Giờ VN (0-23), không phải UTC: `updatePapersAt` lưu giờ UTC trong cột
	 * `timestamp without time zone`, server đã cộng 7 giờ trước khi tách ra —
	 * gửi thẳng UTC lên biểu đồ sẽ lệch múi giờ với các bộ lọc ngày khác trên
	 * cùng trang (vốn đều quy về Asia/Ho_Chi_Minh).
	 */
	hour: number;
	/** Mốc đầu của ô 30 phút trong giờ đó: 0 hoặc 30. */
	minute: number;
	count: number;
}

export interface ReceptionTimelineDay {
	date: string;
	count: number;
	cumulative: number;
}

/** Một người tiếp nhận cùng lưới nhiệt độ riêng của họ. */
export interface ReceptionTimelineApprover {
	/** `MaCB` khi có, nếu không thì id tài khoản — cùng quy ước với tab Người tiếp nhận. */
	approverId: string;
	approverName: string;
	totalReceived: number;
	buckets: ReceptionTimelineBucket[];
}

export interface ReceptionTimeline {
	buckets: ReceptionTimelineBucket[];
	days: ReceptionTimelineDay[];
	/**
	 * Chỉ gồm hồ sơ có người sửa cuối, nên tổng của các phần tử ở đây có thể NHỎ
	 * HƠN `totalReceived`. Server đã sắp giảm dần theo `totalReceived`.
	 */
	approvers: ReceptionTimelineApprover[];
	totalReceived: number;
	/** null khi chưa có hồ sơ nào trong phạm vi lọc — không có giờ nào là "đỉnh". */
	peakHour: number | null;
}

export interface MajorQuotaRow {
	majorCode: string;
	majorName: string;
	facultyCode: string | null;
	/**
	 * Có thể bằng 0: ngành chưa có dòng chỉ tiêu trong bảng `major_quota` vẫn
	 * phải trả về ở đây (server không lọc bỏ) — nếu lược đi thì tổng `rows`
	 * không còn khớp với tổng ở tab Ngành, hai chỗ sẽ hiện hai con số khác nhau
	 * cho cùng một khoá.
	 */
	quota: number;
	admitted: number;
	paid: number;
	received: number;
	/** received/quota*100, 1 chữ số thập phân. */
	fillRate: number;
	/** admitted/quota*100. */
	callRate: number;
}

export interface MajorQuotaSummary {
	rows: MajorQuotaRow[];
	totalQuota: number;
	totalAdmitted: number;
	totalPaid: number;
	totalReceived: number;
	/**
	 * `count(FeeStatus='PAID' AND NOT DaTiepNhan)`, đếm trực tiếp ở backend —
	 * KHÔNG suy ra bằng `totalPaid - totalReceived` ở tầng này. Hai tập không
	 * lồng nhau (có sinh viên đã tiếp nhận nhưng chưa ghi nhận học phí), nên
	 * phép trừ đó cho số thấp hơn thực tế.
	 */
	totalPaidNotReceived: number;
}

export interface PriorityAreaRow {
	/** "1" | "2" | "2NT" | "3" | "" — chuỗi rỗng nghĩa là không xác định được. */
	area: string;
	/** Nhãn hiển thị sẵn, ví dụ "Khu vực 1", "Không xác định". */
	label: string;
	total: number;
	received: number;
	paid: number;
}

export interface PriorityHighSchoolGroup {
	total: number;
	received: number;
	paid: number;
	/** received/total × 100, một chữ số thập phân; total = 0 thì 0. */
	receivedRate: number;
}

/**
 * `GET /statistics/priority-high-schools` — so nhóm sinh viên đến từ 149 trường
 * THPT được ĐHQG-HCM ưu tiên với phần còn lại.
 *
 * CHỈ CÓ NGHĨA VỚI KHOÁ 2026. Khoá 2025 dùng hệ mã trường khác — cùng con số
 * nhưng trỏ sang tỉnh khác — nên áp danh sách này lên khoá cũ sẽ ra số sai mà
 * không có gì báo. Server từ chối tính (trả lỗi) khi bộ lọc khoá không phải
 * 2026; nơi gọi phải hiển thị "chỉ áp dụng cho khoá 2026" thay vì đẩy lỗi đó
 * ra toast như một sự cố.
 *
 * `listSize` là số mã trường trong danh sách (114 mã cho 149 trường — chênh
 * lệch là do một số trường không có thí sinh khoá 2026, và một số trường có hai
 * mã trùng trong danh mục). `matchedSchools` là số trường thực sự có sinh viên
 * trong tập đang lọc.
 */
export interface PriorityHighSchoolStats {
	inList: PriorityHighSchoolGroup;
	notInList: PriorityHighSchoolGroup;
	listSize: number;
	matchedSchools: number;
}

/**
 * Một dòng của `GET /statistics/graduation-years`. `enrolled`/`notEnrolled`
 * trùng giá trị với `received`/`pending` — server suy "đã nhập học" từ cờ
 * `DaTiepNhan`, không có cột nhập học riêng — nhưng vẫn giữ tên field như
 * backend trả để khỏi phải ánh xạ lại ở component.
 */
export interface GraduationYearStatistics {
	graduationYear: number;
	total: number;
	received: number;
	pending: number;
	paid: number;
	unpaid: number;
	enrolled: number;
	notEnrolled: number;
}

/** Một cột trong histogram điểm học bạ, thang 30, mỗi bin rộng 1 điểm. */
export interface HocBaHistogramBin {
	from: number;
	to: number;
	total: number;
}

export interface HocBaByMajor {
	majorCode: string;
	majorName: string;
	count: number;
	avgScore: number;
}

/** Điểm trung bình một môn trong tổ hợp, theo từng lớp (thang 10). */
export interface HocBaYearAverage {
	year: 10 | 11 | 12;
	avgSubjectScore: number;
}

/**
 * `GET /statistics/hoc-ba-scores`.
 *
 * Đây là **điểm học bạ tổ hợp thang 30** (trung bình 3 năm lớp 10/11/12 của 3
 * môn trong tổ hợp), **KHÔNG PHẢI điểm đầu vào**: công thức tuyển sinh thật là
 * `Điểm học lực = 0.8 × (THPT hoặc ĐGNL) + 0.2 × học bạ`, và phần 0,8 không
 * tồn tại trong cơ sở dữ liệu này — nó chỉ có phần học bạ (20%). Mọi nơi hiển
 * thị số này BẮT BUỘC dùng nhãn "Điểm học bạ tổ hợp (thang 30)", không được
 * gọi "điểm đầu vào" / "điểm trúng tuyển" / "điểm xét tuyển".
 *
 * `skipped` đếm hồ sơ KHÔNG tính được điểm: khoá 2025 chỉ điền `Diem_Lop12`
 * (2.901/4.804 hồ sơ), thiếu `Diem_Lop10`/`Diem_Lop11` nên không tách được đủ
 * 3 năm × 3 môn. `counted + skipped` mới bằng tổng hồ sơ trong phạm vi lọc.
 */
export interface HocBaScoreStats {
	histogram: HocBaHistogramBin[];
	byMajor: HocBaByMajor[];
	byYear: HocBaYearAverage[];
	overallAverage: number;
	/** Số hồ sơ tính được điểm. */
	counted: number;
	/** Số hồ sơ thiếu/không tách được, xem comment trên interface. */
	skipped: number;
}

export interface SubjectCombinationRow {
	/** VD "Toán; Vật lí; Tin học". */
	combination: string;
	/**
	 * Mã tổ hợp xét tuyển của Bộ ("A00", "X06"...), `null` khi bộ môn này không
	 * có mã chuẩn. Chỉ là cách gọi tên cho bộ ba môn — tổ hợp ở đây vẫn là tổ
	 * hợp hệ thống tự chọn theo điểm học bạ, không phải tổ hợp đăng ký.
	 */
	code: string | null;
	subjects: string[];
	total: number;
}

/** Một dòng của bảng mã tổ hợp chuẩn (dữ liệu tra cứu, không phụ thuộc bộ lọc). */
export interface CombinationCatalogEntry {
	code: string;
	subjects: string[];
}

export interface SubjectCombinationByMajor {
	majorCode: string;
	majorName: string;
	combinations: { combination: string; total: number }[];
}

/**
 * `GET /statistics/subject-combinations`.
 *
 * Tổ hợp ở đây là tổ hợp **hệ thống tự chọn để tổng điểm học bạ lớn nhất**,
 * KHÔNG PHẢI tổ hợp thí sinh đăng ký nguyện vọng — nên số liệu này đo *thế
 * mạnh học bạ* của thí sinh, không đo nguyện vọng của họ. Không được gắn nhãn
 * "tổ hợp đăng ký" hay suy ra ý định xét tuyển từ đây.
 */
export interface SubjectCombinationStats {
	/** Sắp giảm dần theo `total`. */
	rows: SubjectCombinationRow[];
	byMajor: SubjectCombinationByMajor[];
}

/** Điểm trung bình MỘT môn ở MỘT lớp (thang 10). Xem `SubjectScoreRow.byYear`. */
export interface SubjectYearAverage {
	year: 10 | 11 | 12;
	avgScore: number;
}

/**
 * Một dòng của `GET /statistics/subject-scores` — gom theo TÊN MÔN (Toán, Vật
 * lí, ...) chứ không theo tổ hợp như `HocBaByMajor`/`SubjectCombinationRow`.
 *
 * `avgScore`/`minScore`/`maxScore` là **điểm học bạ trung bình 3 năm của môn
 * đó, thang 10** — KHÔNG PHẢI điểm đầu vào/trúng tuyển/điểm chuẩn (xem ràng
 * buộc đặt tên ở pha 09: phần 0,8 công thức thật là THPT/ĐGNL, không có trong
 * cơ sở dữ liệu này).
 *
 * `count`/`share`: một môn như Toán có mặt ở 100% hồ sơ (mọi tổ hợp đều dùng
 * Toán), trong khi các môn đuôi (Lịch sử, Văn, CNNN, Địa lí, CNCN) đều dưới
 * 5%. So sánh điểm TB của hai nhóm này mà không nhìn `count`/`share` bên cạnh
 * sẽ đọc sai — mẫu vài trăm không đáng tin bằng mẫu vài nghìn.
 */
export interface SubjectScoreRow {
	subject: string;
	count: number;
	/** count/totalStudents*100, 1 chữ số thập phân. */
	share: number;
	avgScore: number;
	minScore: number;
	maxScore: number;
	byYear: SubjectYearAverage[];
}

export interface SubjectScoreStats {
	/** Sắp giảm dần theo `count`. */
	rows: SubjectScoreRow[];
	/** Mẫu số của `share`. */
	totalStudents: number;
}

class StatisticsAPIService {
	private buildQueryParams(filter?: StatisticsFilter): string {
		const params = new URLSearchParams();

		if (filter) {
			params.append("type", filter.type);
			if (filter.limit) params.append("limit", filter.limit.toString());
			if (filter.offset) params.append("offset", filter.offset.toString());
			if (filter.sortBy) params.append("sortBy", filter.sortBy);

			// Date-only filters: YYYY-MM-DD in Asia/Ho_Chi_Minh, never a UTC instant.
			if (filter.receptionDateFrom) {
				params.append("receptionDateFrom", toHoChiMinhDateString(filter.receptionDateFrom));
			}
			if (filter.receptionDateTo) {
				params.append("receptionDateTo", toHoChiMinhDateString(filter.receptionDateTo));
			}
			if (filter.appointmentSlot) {
				params.append("appointmentSlot", filter.appointmentSlot);
			}
			// Snake case, unlike its neighbours: this one is read by `scopeFaculty`
			// rather than by the statistics controller, and that middleware names it
			// after the `faculty_code` column it compares against.
			if (filter.facultyCode) {
				params.append("faculty_code", filter.facultyCode);
			}
			if (filter.admissionYear) {
				params.append("admissionYear", filter.admissionYear);
			}
		}

		return params.toString();
	}

	async getOverallStats(filter?: StatisticsFilter): Promise<OverallStatistics> {
		try {
			let url = "/statistics/overall";
			const queryString = this.buildQueryParams(filter);
			if (queryString) {
				url += "?" + queryString;
			}

			const response = await AuthenticatedAPIClient.get(url);

			if (!response.success) {
				throw new Error(response.message || "Failed to get overall statistics");
			}

			return response.data as OverallStatistics;
		} catch (error) {
			if (error instanceof Error && error.message.includes("Authentication required")) {
				throw error;
			}

			if (error instanceof Error) {
				throw error;
			}
			throw new Error("Failed to fetch overall statistics");
		}
	}

	/**
	 * Tất cả số liệu trang thống kê cần khi tải, trong một lần gọi.
	 *
	 * Trang này từng bắn sáu request song song. Mỗi request mở một giao dịch
	 * riêng dưới database, nên chúng là sáu ảnh chụp ở sáu thời điểm khác nhau —
	 * thẻ tổng quan và tab ngành có thể hiển thị hai trạng thái khác nhau của
	 * cùng một cơ sở dữ liệu, và không có gì trên giao diện cho biết điều đó.
	 * Một request là một giao dịch, một ảnh chụp, và cũng là một connection thay
	 * vì sáu.
	 *
	 * Chỉ nhận bộ lọc, không nhận `type`/`sortBy`/phân trang: đây là lần tải đầu.
	 * Các tab đổi bộ lọc riêng của mình vẫn gọi endpoint lẻ như cũ.
	 */
	async getDashboard(filter?: StatisticsFilter): Promise<DashboardStatistics> {
		let url = "/statistics/dashboard";
		const queryString = this.buildQueryParams(filter);
		if (queryString) url += "?" + queryString;

		const response = await AuthenticatedAPIClient.get(url);
		if (!response.success) {
			throw new Error(response.message || "Failed to get dashboard statistics");
		}

		return response.data as DashboardStatistics;
	}

	async getStatsByDepartment(filter?: StatisticsFilter): Promise<StatisticsByDepartment[]> {
		try {
			let url = "/statistics/departments";
			const queryString = this.buildQueryParams(filter);
			if (queryString) {
				url += "?" + queryString;
			}

			const response = await AuthenticatedAPIClient.get(url);

			if (!response.success) {
				throw new Error(response.message || "Failed to get department statistics");
			}

			return response.data as StatisticsByDepartment[];
		} catch (error) {
			if (error instanceof Error && error.message.includes("Authentication required")) {
				throw error;
			}

			if (error instanceof Error) {
				throw error;
			}
			throw new Error("Failed to fetch department statistics");
		}
	}

	async getStatsByApprover(filter?: StatisticsFilter): Promise<StatisticsByApprover[]> {
		try {
			let url = "/statistics/approvers";
			const queryString = this.buildQueryParams(filter);
			if (queryString) {
				url += "?" + queryString;
			}

			const response = await AuthenticatedAPIClient.get(url);

			if (!response.success) {
				throw new Error(response.message || "Failed to get approver statistics");
			}

			return response.data as StatisticsByApprover[];
		} catch (error) {
			if (error instanceof Error && error.message.includes("Authentication required")) {
				throw error;
			}

			if (error instanceof Error) {
				throw error;
			}
			throw new Error("Failed to fetch approver statistics");
		}
	}

	async getStatsByProvince(
		filter?: StatisticsFilter,
	): Promise<PaginatedResponse<StatisticsByProvince>> {
		try {
			let url = "/statistics/provinces";
			const queryString = this.buildQueryParams(filter);
			if (queryString) {
				url += "?" + queryString;
			}

			const response = await AuthenticatedAPIClient.get(url);

			if (!response.success) {
				throw new Error(response.message || "Failed to get province statistics");
			}

			return response.data as PaginatedResponse<StatisticsByProvince>;
		} catch (error) {
			if (error instanceof Error && error.message.includes("Authentication required")) {
				throw error;
			}

			if (error instanceof Error) {
				throw error;
			}
			throw new Error("Failed to fetch province statistics");
		}
	}

	async getStatsByHighSchool(
		filter?: StatisticsFilter,
	): Promise<PaginatedResponse<StatisticsByHighSchool>> {
		try {
			let url = "/statistics/high-schools";
			const queryString = this.buildQueryParams(filter);
			if (queryString) {
				url += "?" + queryString;
			}

			const response = await AuthenticatedAPIClient.get(url);

			if (!response.success) {
				throw new Error(response.message || "Failed to get high school statistics");
			}

			return response.data as PaginatedResponse<StatisticsByHighSchool>;
		} catch (error) {
			if (error instanceof Error && error.message.includes("Authentication required")) {
				throw error;
			}

			if (error instanceof Error) {
				throw error;
			}
			throw new Error("Failed to fetch high school statistics");
		}
	}

	async getStatsByFaculty(filter?: StatisticsFilter): Promise<StatisticsByFaculty[]> {
		try {
			let url = "/statistics/faculties";
			const queryString = this.buildQueryParams(filter);
			if (queryString) {
				url += "?" + queryString;
			}

			const response = await AuthenticatedAPIClient.get(url);

			if (!response.success) {
				throw new Error(response.message || "Failed to get faculty statistics");
			}

			return response.data as StatisticsByFaculty[];
		} catch (error) {
			if (error instanceof Error && error.message.includes("Authentication required")) {
				throw error;
			}

			if (error instanceof Error) {
				throw error;
			}
			throw new Error("Failed to fetch faculty statistics");
		}
	}

	async getGenderDistribution(filter?: StatisticsFilter): Promise<GenderDistribution> {
		try {
			let url = "/statistics/gender-distribution";
			const queryString = this.buildQueryParams(filter);
			if (queryString) {
				url += "?" + queryString;
			}

			const response = await AuthenticatedAPIClient.get(url);

			if (!response.success) {
				throw new Error(response.message || "Failed to get gender distribution");
			}

			return response.data as GenderDistribution;
		} catch (error) {
			if (error instanceof Error && error.message.includes("Authentication required")) {
				throw error;
			}

			if (error instanceof Error) {
				throw error;
			}
			throw new Error("Failed to fetch gender distribution");
		}
	}

	async getStatsByAdmissionMethod(
		filter?: StatisticsFilter,
	): Promise<StatisticsByAdmissionMethod[]> {
		try {
			let url = "/statistics/admission-methods";
			const queryString = this.buildQueryParams(filter);
			if (queryString) {
				url += "?" + queryString;
			}

			const response = await AuthenticatedAPIClient.get(url);

			if (!response.success) {
				throw new Error(response.message || "Failed to get admission method statistics");
			}

			return response.data as StatisticsByAdmissionMethod[];
		} catch (error) {
			if (error instanceof Error && error.message.includes("Authentication required")) {
				throw error;
			}

			if (error instanceof Error) {
				throw error;
			}
			throw new Error("Failed to fetch admission method statistics");
		}
	}

	async getReceptionTimeline(filter?: StatisticsFilter): Promise<ReceptionTimeline> {
		try {
			let url = "/statistics/reception-timeline";
			const queryString = this.buildQueryParams(filter);
			if (queryString) {
				url += "?" + queryString;
			}

			const response = await AuthenticatedAPIClient.get(url);

			if (!response.success) {
				throw new Error(response.message || "Failed to get reception timeline");
			}

			return response.data as ReceptionTimeline;
		} catch (error) {
			if (error instanceof Error && error.message.includes("Authentication required")) {
				throw error;
			}

			if (error instanceof Error) {
				throw error;
			}
			throw new Error("Failed to fetch reception timeline");
		}
	}

	async getMajorQuota(filter?: StatisticsFilter): Promise<MajorQuotaSummary> {
		try {
			let url = "/statistics/major-quota";
			const queryString = this.buildQueryParams(filter);
			if (queryString) {
				url += "?" + queryString;
			}

			const response = await AuthenticatedAPIClient.get(url);

			if (!response.success) {
				throw new Error(response.message || "Failed to get major quota statistics");
			}

			return response.data as MajorQuotaSummary;
		} catch (error) {
			if (error instanceof Error && error.message.includes("Authentication required")) {
				throw error;
			}

			if (error instanceof Error) {
				throw error;
			}
			throw new Error("Failed to fetch major quota statistics");
		}
	}

	async getPriorityAreas(filter?: StatisticsFilter): Promise<PriorityAreaRow[]> {
		try {
			let url = "/statistics/priority-areas";
			const queryString = this.buildQueryParams(filter);
			if (queryString) {
				url += "?" + queryString;
			}

			const response = await AuthenticatedAPIClient.get(url);

			if (!response.success) {
				throw new Error(response.message || "Failed to get priority area statistics");
			}

			return response.data as PriorityAreaRow[];
		} catch (error) {
			if (error instanceof Error && error.message.includes("Authentication required")) {
				throw error;
			}

			if (error instanceof Error) {
				throw error;
			}
			throw new Error("Failed to fetch priority area statistics");
		}
	}

	async getHocBaScores(filter?: StatisticsFilter): Promise<HocBaScoreStats> {
		try {
			let url = "/statistics/hoc-ba-scores";
			const queryString = this.buildQueryParams(filter);
			if (queryString) {
				url += "?" + queryString;
			}

			const response = await AuthenticatedAPIClient.get(url);

			if (!response.success) {
				throw new Error(response.message || "Failed to get hoc ba score statistics");
			}

			return response.data as HocBaScoreStats;
		} catch (error) {
			if (error instanceof Error && error.message.includes("Authentication required")) {
				throw error;
			}

			if (error instanceof Error) {
				throw error;
			}
			throw new Error("Failed to fetch hoc ba score statistics");
		}
	}

	/**
	 * Tra bảng mã tổ hợp chuẩn theo từ khoá tự do (mã hoặc tên môn). Không nhận
	 * bộ lọc thống kê: đây là dữ liệu quy chế, không phải số liệu thí sinh.
	 * Việc so khớp làm ở server — xem `lib/subject-combination-codes.ts` bên BE.
	 */
	async getCombinationCatalog(query?: string): Promise<CombinationCatalogEntry[]> {
		try {
			const url = query?.trim()
				? `/statistics/combination-catalog?q=${encodeURIComponent(query.trim())}`
				: "/statistics/combination-catalog";
			const response = await AuthenticatedAPIClient.get(url);

			if (!response.success) {
				throw new Error(response.message || "Failed to get combination catalog");
			}

			return response.data as CombinationCatalogEntry[];
		} catch (error) {
			if (error instanceof Error) {
				throw error;
			}
			throw new Error("Failed to fetch combination catalog");
		}
	}

	async getSubjectCombinations(filter?: StatisticsFilter): Promise<SubjectCombinationStats> {
		try {
			let url = "/statistics/subject-combinations";
			const queryString = this.buildQueryParams(filter);
			if (queryString) {
				url += "?" + queryString;
			}

			const response = await AuthenticatedAPIClient.get(url);

			if (!response.success) {
				throw new Error(response.message || "Failed to get subject combination statistics");
			}

			return response.data as SubjectCombinationStats;
		} catch (error) {
			if (error instanceof Error && error.message.includes("Authentication required")) {
				throw error;
			}

			if (error instanceof Error) {
				throw error;
			}
			throw new Error("Failed to fetch subject combination statistics");
		}
	}

	async getSubjectScores(filter?: StatisticsFilter): Promise<SubjectScoreStats> {
		try {
			let url = "/statistics/subject-scores";
			const queryString = this.buildQueryParams(filter);
			if (queryString) {
				url += "?" + queryString;
			}

			const response = await AuthenticatedAPIClient.get(url);

			if (!response.success) {
				throw new Error(response.message || "Failed to get subject score statistics");
			}

			return response.data as SubjectScoreStats;
		} catch (error) {
			if (error instanceof Error && error.message.includes("Authentication required")) {
				throw error;
			}

			if (error instanceof Error) {
				throw error;
			}
			throw new Error("Failed to fetch subject score statistics");
		}
	}

	async getPriorityHighSchools(filter?: StatisticsFilter): Promise<PriorityHighSchoolStats> {
		try {
			let url = "/statistics/priority-high-schools";
			const queryString = this.buildQueryParams(filter);
			if (queryString) {
				url += "?" + queryString;
			}

			const response = await AuthenticatedAPIClient.get(url);

			if (!response.success) {
				throw new Error(response.message || "Failed to get priority high school statistics");
			}

			return response.data as PriorityHighSchoolStats;
		} catch (error) {
			if (error instanceof Error && error.message.includes("Authentication required")) {
				throw error;
			}

			if (error instanceof Error) {
				throw error;
			}
			throw new Error("Failed to fetch priority high school statistics");
		}
	}

	async getStatsByGraduationYear(filter?: StatisticsFilter): Promise<GraduationYearStatistics[]> {
		try {
			let url = "/statistics/graduation-years";
			const queryString = this.buildQueryParams(filter);
			if (queryString) {
				url += "?" + queryString;
			}

			const response = await AuthenticatedAPIClient.get(url);

			if (!response.success) {
				throw new Error(response.message || "Failed to get graduation year statistics");
			}

			return response.data as GraduationYearStatistics[];
		} catch (error) {
			if (error instanceof Error && error.message.includes("Authentication required")) {
				throw error;
			}

			if (error instanceof Error) {
				throw error;
			}
			throw new Error("Failed to fetch graduation year statistics");
		}
	}

	/**
	 * Takes only `admissionYear`, not the full `StatisticsFilter` — this route
	 * has no `type` (it returns a flat list, not a breakdown), and
	 * `StatisticsFilter.type` is required, so reusing that interface here would
	 * force every caller to invent a meaningless `type` just to pass a khoá.
	 */
	async getAppointmentSlots(filter?: Pick<StatisticsFilter, "admissionYear">): Promise<string[]> {
		try {
			let url = "/statistics/appointment-slots";
			const params = new URLSearchParams();
			if (filter?.admissionYear) params.append("admissionYear", filter.admissionYear);
			const queryString = params.toString();
			if (queryString) {
				url += "?" + queryString;
			}

			const response = await AuthenticatedAPIClient.get(url);

			if (!response.success) {
				throw new Error(response.message || "Failed to get appointment slots");
			}

			return response.data as string[];
		} catch (error) {
			if (error instanceof Error && error.message.includes("Authentication required")) {
				throw error;
			}

			if (error instanceof Error) {
				throw error;
			}
			throw new Error("Failed to fetch appointment slots");
		}
	}

	async exportProvinceStatsAsCSV(filter?: StatisticsFilter): Promise<void> {
		try {
			const baseURL = "/v1";
			// The download has to match the screen it was taken from: without the
			// filters, a khoa-narrowed, date-filtered dashboard silently exports
			// every khoa for all time.
			const query = this.buildQueryParams(filter);
			const response = await fetch(
				`${baseURL}/statistics/provinces/export${query ? `?${query}` : ""}`,
				{
					method: "GET",
					headers: {
						Accept: "text/csv",
					},
					credentials: "include",
				},
			);

			if (!response.ok) {
				throw new Error(`HTTP error! status: ${response.status}`);
			}

			// Get the filename from the Content-Disposition header if available
			const contentDisposition = response.headers.get("Content-Disposition");
			const filename = contentDisposition
				? contentDisposition.split("filename=")[1]?.replace(/"/g, "")
				: "thong-ke-theo-tinh.csv";

			// Download the file
			const blob = await response.blob();
			const url = window.URL.createObjectURL(blob);
			const a = document.createElement("a");
			a.href = url;
			a.download = filename;
			document.body.appendChild(a);
			a.click();
			document.body.removeChild(a);
			window.URL.revokeObjectURL(url);
		} catch {
			throw new Error("Failed to export province statistics CSV");
		}
	}

	async exportHighSchoolStatsAsCSV(filter?: StatisticsFilter): Promise<void> {
		try {
			const baseURL = "/v1";
			// The download has to match the screen it was taken from: without the
			// filters, a khoa-narrowed, date-filtered dashboard silently exports
			// every khoa for all time.
			const query = this.buildQueryParams(filter);
			const response = await fetch(
				`${baseURL}/statistics/high-schools/export${query ? `?${query}` : ""}`,
				{
					method: "GET",
					headers: {
						Accept: "text/csv",
					},
					credentials: "include",
				},
			);

			if (!response.ok) {
				throw new Error(`HTTP error! status: ${response.status}`);
			}

			// Get the filename from the Content-Disposition header if available
			const contentDisposition = response.headers.get("Content-Disposition");
			const filename = contentDisposition
				? contentDisposition.split("filename=")[1]?.replace(/"/g, "")
				: "thong-ke-theo-truong-thpt.csv";

			// Download the file
			const blob = await response.blob();
			const url = window.URL.createObjectURL(blob);
			const a = document.createElement("a");
			a.href = url;
			a.download = filename;
			document.body.appendChild(a);
			a.click();
			document.body.removeChild(a);
			window.URL.revokeObjectURL(url);
		} catch {
			throw new Error("Failed to export high school statistics CSV");
		}
	}

	async exportAllStudentsAsCSV(): Promise<void> {
		try {
			const baseURL = "/v1";
			const response = await fetch(`${baseURL}/statistics/all-students/export`, {
				method: "GET",
				headers: {
					Accept: "text/csv",
				},
				credentials: "include",
			});

			if (!response.ok) {
				throw new Error(`HTTP error! status: ${response.status}`);
			}

			// Get the filename from the Content-Disposition header if available
			const contentDisposition = response.headers.get("Content-Disposition");
			const filename = contentDisposition
				? contentDisposition.split("filename=")[1]?.replace(/"/g, "")
				: "toan-bo-du-lieu-sinh-vien.csv";

			// Download the file
			const blob = await response.blob();
			const url = window.URL.createObjectURL(blob);
			const a = document.createElement("a");
			a.href = url;
			a.download = filename;
			document.body.appendChild(a);
			a.click();
			document.body.removeChild(a);
			window.URL.revokeObjectURL(url);
		} catch {
			throw new Error("Failed to export all students CSV");
		}
	}

	/**
	 * Cùng dữ liệu với bản CSV, nhưng là file Excel mở ra đọc được ngay.
	 *
	 * Quyền không theo role mà theo `export_excel` trong `permissions` của từng tài khoản; server
	 * trả 403 nếu tài khoản chưa được cấp.
	 */
	async exportAllStudentsAsXLSX(
		filter?: Pick<StatisticsFilter, "admissionYear" | "facultyCode">,
	): Promise<void> {
		try {
			const baseURL = "/v1";
			const params = new URLSearchParams();
			if (filter?.admissionYear) params.append("admissionYear", filter.admissionYear);
			// `faculty_code` (snake case) chứ không phải `facultyCode`: tham số này do
			// middleware `scopeFaculty` đọc, và nó gọi theo đúng tên cột nó so sánh.
			//
			// Thiếu nó thì file tải về KHÔNG khớp màn hình đang xem: một admin đã chọn
			// một khoa trên dropdown vẫn nhận về dữ liệu của mọi khoa. Tài khoản bị
			// giới hạn khoa thì không thấy lỗi này — phạm vi của họ đến từ phiên đăng
			// nhập chứ không từ tham số — nên nó chỉ lộ ra với admin và Ban giám hiệu,
			// đúng những người tải file nhiều nhất.
			if (filter?.facultyCode) params.append("faculty_code", filter.facultyCode);
			const query = params.toString();
			const response = await fetch(
				`${baseURL}/statistics/all-students/export/xlsx${query ? `?${query}` : ""}`,
				{
					method: "GET",
					headers: {
						Accept: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
					},
					credentials: "include",
				},
			);

			if (!response.ok) {
				throw new Error(`HTTP error! status: ${response.status}`);
			}

			const contentDisposition = response.headers.get("Content-Disposition");
			const filename = contentDisposition
				? contentDisposition.split("filename=")[1]?.replace(/"/g, "")
				: "toan-bo-du-lieu-sinh-vien.xlsx";

			const blob = await response.blob();
			const url = window.URL.createObjectURL(blob);
			const a = document.createElement("a");
			a.href = url;
			a.download = filename;
			document.body.appendChild(a);
			a.click();
			document.body.removeChild(a);
			window.URL.revokeObjectURL(url);
		} catch {
			throw new Error("Failed to export all students XLSX");
		}
	}

	/**
	 * Tải nhịp độ tiếp nhận dạng `.xlsx` — mỗi ngày một sheet.
	 *
	 * Gửi cùng bộ lọc với lưới đang hiện (trừ phạm vi ngày, thứ tab này không
	 * dùng) để file khớp màn hình. Quyền `view_pacing` kiểm ở server; tài khoản
	 * chưa được cấp nhận 403.
	 */
	async exportReceptionTimelineAsXLSX(filter?: StatisticsFilter): Promise<void> {
		const query = this.buildQueryParams(filter);
		const response = await fetch(
			`/v1/statistics/reception-timeline/export${query ? `?${query}` : ""}`,
			{
				method: "GET",
				headers: {
					Accept: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
				},
				credentials: "include",
			},
		);

		if (!response.ok) {
			throw new Error(`HTTP error! status: ${response.status}`);
		}

		const contentDisposition = response.headers.get("Content-Disposition");
		const filename = contentDisposition
			? (contentDisposition.split("filename=")[1]?.replace(/"/g, "") ?? "nhip-do-tiep-nhan.xlsx")
			: "nhip-do-tiep-nhan.xlsx";

		const blob = await response.blob();
		const url = window.URL.createObjectURL(blob);
		const a = document.createElement("a");
		a.href = url;
		a.download = filename;
		document.body.appendChild(a);
		a.click();
		document.body.removeChild(a);
		window.URL.revokeObjectURL(url);
	}

	async exportReportAsCSV(): Promise<void> {
		try {
			const baseURL = "/v1";
			const response = await fetch(`${baseURL}/statistics/received-students-report/export`, {
				method: "GET",
				headers: {
					Accept: "text/csv",
				},
				credentials: "include",
			});

			if (!response.ok) {
				throw new Error(`HTTP error! status: ${response.status}`);
			}

			// Get the filename from the Content-Disposition header if available
			const contentDisposition = response.headers.get("Content-Disposition");
			const filename = contentDisposition
				? contentDisposition.split("filename=")[1]?.replace(/"/g, "")
				: "bao-cao-sinh-vien-da-tiep-nhan.csv";

			// Download the file
			const blob = await response.blob();
			const url = window.URL.createObjectURL(blob);
			const a = document.createElement("a");
			a.href = url;
			a.download = filename;
			document.body.appendChild(a);
			a.click();
			document.body.removeChild(a);
			window.URL.revokeObjectURL(url);
		} catch {
			throw new Error("Failed to export report CSV");
		}
	}

	/**
	 * Backend chấp nhận `limit`/`offset`/`receptionDateFrom`/`receptionDateTo`/
	 * `appointmentSlot` cho export này (`statistics.controller.ts` ->
	 * `extractFilterParams`), nhưng KHÔNG nhận `admissionYear` — hợp đồng đó bị
	 * đóng băng có chủ đích, nên chữ ký ở đây loại `admissionYear` ra thay vì
	 * dùng `StatisticsFilter` đầy đủ.
	 */
	async exportFacultyStatsAsCSV(
		filter?: Pick<
			StatisticsFilter,
			"limit" | "offset" | "receptionDateFrom" | "receptionDateTo" | "appointmentSlot"
		>,
	) {
		try {
			const baseURL = "/v1";
			const params = new URLSearchParams();
			if (filter?.limit) params.append("limit", filter.limit.toString());
			if (filter?.offset) params.append("offset", filter.offset.toString());
			if (filter?.receptionDateFrom) {
				params.append("receptionDateFrom", toHoChiMinhDateString(filter.receptionDateFrom));
			}
			if (filter?.receptionDateTo) {
				params.append("receptionDateTo", toHoChiMinhDateString(filter.receptionDateTo));
			}
			if (filter?.appointmentSlot) params.append("appointmentSlot", filter.appointmentSlot);
			const query = params.toString();
			const response = await fetch(
				`${baseURL}/statistics/faculties/export${query ? `?${query}` : ""}`,
				{
					method: "GET",
					headers: {
						Accept: "text/csv",
					},
					credentials: "include",
				},
			);

			if (!response.ok) {
				throw new Error(`HTTP error! status: ${response.status}`);
			}

			// Get the filename from the Content-Disposition header if available
			const contentDisposition = response.headers.get("Content-Disposition");
			const filename = contentDisposition
				? contentDisposition.split("filename=")[1]?.replace(/"/g, "")
				: "thong-ke-theo-khoa.csv";

			// Download the file
			const blob = await response.blob();
			const url = window.URL.createObjectURL(blob);
			const a = document.createElement("a");
			a.href = url;
			a.download = filename;
			document.body.appendChild(a);
			a.click();
			document.body.removeChild(a);
			window.URL.revokeObjectURL(url);
		} catch {
			throw new Error("Failed to export faculty stats CSV");
		}
	}
	// Ba mục của tab "Khác" dưới đây nhận `filter` như mọi endpoint khác. Trước
	// đây chúng không nhận gì, nên backend đã lọc sẵn theo khoá mà frontend không
	// gửi tham số — ba mục này hiện số của MỌI khoá ngay cạnh các tab đã lọc.
	// Một trang thống kê mà mỗi mục một tập dân số thì con số nào cũng vô nghĩa.
	async getAgeDistribution(filter?: StatisticsFilter): Promise<AgeDistributionData[]> {
		try {
			const qs = this.buildQueryParams(filter);
			const response = await AuthenticatedAPIClient.get(
				`/statistics/age-distribution${qs ? `?${qs}` : ""}`,
			);

			if (!response.success) {
				throw new Error(response.message || "Failed to get age distribution");
			}

			return response.data as AgeDistributionData[];
		} catch (error) {
			if (error instanceof Error && error.message.includes("Authentication required")) {
				throw error;
			}

			if (error instanceof Error) {
				throw error;
			}
			throw new Error("Failed to fetch age distribution");
		}
	}

	async getAwardStatistics(filter?: StatisticsFilter): Promise<AwardData[]> {
		try {
			const qs = this.buildQueryParams(filter);
			const response = await AuthenticatedAPIClient.get(`/statistics/awards${qs ? `?${qs}` : ""}`);

			if (!response.success) {
				throw new Error(response.message || "Failed to get award statistics");
			}

			return response.data as AwardData[];
		} catch (error) {
			if (error instanceof Error && error.message.includes("Authentication required")) {
				throw error;
			}

			if (error instanceof Error) {
				throw error;
			}
			throw new Error("Failed to fetch award statistics");
		}
	}

	async getProvinceStatistics(filter?: StatisticsFilter): Promise<StatisticsByProvince[]> {
		try {
			const qs = this.buildQueryParams(filter);
			const response = await AuthenticatedAPIClient.get(
				`/statistics/province-demographics${qs ? `?${qs}` : ""}`,
			);

			if (!response.success) {
				throw new Error(response.message || "Failed to get province demographics");
			}

			return response.data as StatisticsByProvince[];
		} catch (error) {
			if (error instanceof Error && error.message.includes("Authentication required")) {
				throw error;
			}

			if (error instanceof Error) {
				throw error;
			}
			throw new Error("Failed to fetch province demographics");
		}
	}
}

export const statisticsAPIService = new StatisticsAPIService();
export default statisticsAPIService;
